import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/Button';
import { subscribe } from '../lib/mailchimp';
import './Newsletter.css';

type Status = 'idle' | 'loading' | 'success' | 'error';

export function Newsletter() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    setErrorMsg('');
    const result = await subscribe(email.trim());
    if (result.ok) {
      setStatus('success');
      setEmail('');
      return;
    }
    setStatus('error');
    setErrorMsg(
      result.error === 'invalid_email'
        ? t('newsletter.errorInvalidEmail')
        : t('newsletter.errorGeneric'),
    );
  }

  return (
    <section id="newsletter" className="section section--dark">
      <div className="container container-narrow">
        <div className="section-header">
          <span className="eyebrow">{t('newsletter.eyebrow')}</span>
          <h2>{t('newsletter.title')}</h2>
          <p>{t('newsletter.subtitle')}</p>
        </div>

        {status === 'success' ? (
          <div className="newsletter-success">
            <span className="newsletter-success__check" aria-hidden="true">✓</span>
            <h3>{t('newsletter.successTitle')}</h3>
            <p>{t('newsletter.successBody')}</p>
          </div>
        ) : (
          <form className="newsletter-form" onSubmit={onSubmit} noValidate>
            <label className="newsletter-form__field">
              <span className="visually-hidden">{t('newsletter.emailLabel')}</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('newsletter.emailPlaceholder')}
                aria-label={t('newsletter.emailLabel')}
                aria-invalid={status === 'error'}
                disabled={status === 'loading'}
              />
            </label>
            <Button type="submit" size="lg" disabled={status === 'loading'}>
              {status === 'loading' ? t('newsletter.submitting') : t('newsletter.submit')}
            </Button>
          </form>
        )}

        {status === 'error' && <p className="newsletter-error" role="alert">{errorMsg}</p>}

        <p className="newsletter-privacy">{t('newsletter.privacy')}</p>
      </div>
    </section>
  );
}
